// THE MATERIAL FORGE — the client half. Wiring, not generation.
//
// WHAT THIS FILE USED TO BE. Seventeen hundred lines that made every PBR
// surface in the game at load: adobe stucco, weathered plank, wrought iron,
// brass, canvas, stone, paving, roof tile, packed dirt, sand, dirty glass.
// It worked, and the comments in it are five waves of hard-won failures, but
// it worked under a rule nobody asked for — no art files, everything
// generated in code at runtime — and that rule set the ceiling on all of it.
// A forge that has to finish while a player waits makes small, cheap, simple
// textures.
//
// WHAT IT IS NOW. The generators moved, whole, to `@bebop/forge` (tools/forge)
// where they run OFFLINE with no clock on them; tools/assets bakes the ones
// the catalog marks `ship` at high resolution, encodes them to KTX2 with real
// mip chains, and commits them. This file kept the three.js half:
//
//   • `surface()` — hand out a surface, whether it was baked offline (loaded
//     by render/assets.ts before the game boots) or is still forged here;
//   • `triplanar()` — the world-space projection that lets one material dress
//     a box, a cylinder and an icosahedron boulder at the same texel density;
//   • `matchLuma()`, `dress()`, `paint()` — the grade-preserving wiring.
//
// A surface leaves the runtime column by getting a `ship` size in the catalog
// (tools/forge/src/catalog.ts) and its generator being dropped from
// tools/forge/src/runtime.ts. As of the pipeline landing that is the STUCCO,
// which is 2048² instead of 512², costs nothing at load, and has no second
// source anywhere in the client.
//
// PERFORMANCE IS A GATE, NOT AN ASPIRATION. Every sheet a surface hands out is
// process-wide, shared by every material and every scene rebuild (the cache
// deliberately outlives Tracker disposal). Nothing is generated per frame.
// Tile sizes are POWERS OF TWO IN METRES on purpose: the floating origin
// rebases on a 128 m grid, so a world-space projection at 1/2/4 m shifts by a
// whole number of tiles and cannot pop.

import { type AssetResponseEntry, type BakedSurface, bake, SURFACE_CATALOG } from "@bebop/forge";
import { RUNTIME_GENERATORS } from "@bebop/forge/runtime";
import * as THREE from "three";
import { bakedSurface } from "./assets";

export type { BakeOptions as ForgeOptions, Shade, Texel } from "@bebop/forge";
// The noise toolkit lives in @bebop/forge now — it is shared with the offline
// baker so the two cannot drift — but half the render layer imports it from
// here, and moving forty import sites to prove a point is not a change, it is
// churn. This is the one place the re-export is written down.
export {
  cellular,
  fade,
  hash1,
  makeFbm,
  makeFbmAniso,
  makeRidged,
  mulberry,
  noise2,
  noiseOctave,
} from "@bebop/forge";

/**
 * What a surface does with light that no sheet can carry: dielectric
 * reflectance, cloth sheen, and how much of the environment probe it returns.
 *
 * These are per-CLASS constants — a lime wall has one F0 over its whole run —
 * so they ride the manifest as four floats rather than as three more textures.
 * See tools/forge/src/response.ts for the table and the argument.
 *
 * A surface the client still forges itself has no manifest entry, so it gets
 * three's own defaults: F0 0.04, no sheen, probe at face value.
 */
const DEFAULT_RESPONSE: AssetResponseEntry = {
  cls: "default",
  specular: 0.04,
  sheen: 0,
  env: 1,
  rough: [0, 0, 0],
  metal: [0, 0, 0],
  halfMetal: 0,
};

/** One surface: three sheets that agree with each other. */
export interface Surface {
  /** sRGB albedo, cavity already gently baked in. */
  readonly albedo: THREE.Texture;
  /** Tangent-space normal, Sobel-derived from the height field. */
  readonly normal: THREE.Texture;
  /** R = AO, G = roughness, B = metalness (A = height, unsampled). */
  readonly orm: THREE.Texture;
  /** World size of one tile, metres. Powers of two only — see the header. */
  readonly meters: number;
  /** Mean albedo luminance in LINEAR space; `matchLuma` divides by it. */
  readonly meanLinear: number;
  /** The substance: F0, sheen, indirect-specular gain. */
  readonly response: AssetResponseEntry;
}

function toTexture(data: Uint8ClampedArray, size: number, srgb: boolean): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Uint8Array(data.buffer.slice(0)), size, size);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Wrap raw baked sheets in GPU textures. */
export function toSurface(baked: BakedSurface, response?: AssetResponseEntry): Surface {
  return {
    albedo: toTexture(baked.albedo, baked.size, true),
    normal: toTexture(baked.normal, baked.size, false),
    orm: toTexture(baked.orm, baked.size, false),
    meters: baked.meters,
    meanLinear: baked.meanLinear,
    response: response ?? DEFAULT_RESPONSE,
  };
}

/**
 * Forge one surface HERE, at load, from a shade function.
 *
 * Kept for the surfaces this file no longer owns but the client still makes
 * itself: the character forge (render/attire.ts) at garment scale and the
 * city's own sheets (render/city.ts). Both run the same `bake()` the offline
 * pipeline runs — two copies of a Sobel is how two surfaces stop agreeing
 * with each other. Neither is a candidate for shipping yet: attire's sheets
 * are per-character and city's are per-district.
 */
export function forge(
  size: number,
  meters: number,
  shade: Parameters<typeof bake>[2],
  opts: Parameters<typeof bake>[3] = {},
): Surface {
  return toSurface(bake(size, meters, shade, opts));
}

const cache = new Map<string, Surface>();

/**
 * Fetch a surface by catalog name.
 *
 * SHIPPED surfaces come straight out of render/assets.ts, which loaded them
 * before the game booted. FORGED ones are generated on first use into a
 * module-level cache that is never cleared: they cost real milliseconds and
 * are identical for every scene, so they deliberately survive Tracker
 * disposal. A scene teardown must NOT dispose them — `triplanar()` hands them
 * out as uniforms, not as material slots, precisely so a Tracker never sees
 * them.
 */
export function surface(name: string): Surface {
  const hit = cache.get(name);
  if (hit) return hit;
  const spec = SURFACE_CATALOG[name as keyof typeof SURFACE_CATALOG];
  if (!spec) throw new Error(`unknown surface: ${name}`);

  if (spec.ship !== undefined) {
    const baked = bakedSurface(name);
    if (!baked) {
      // Loud on purpose. There is no runtime generator left for a shipped
      // surface, and quietly substituting one would be a second source of
      // truth for its pixels — see the header of render/assets.ts.
      throw new Error(
        `baked surface "${name}" was not loaded; the boot gate should have awaited loadBakedAssets()`,
      );
    }
    cache.set(name, baked);
    return baked;
  }

  const gen = RUNTIME_GENERATORS[spec.gen];
  if (!gen) throw new Error(`no runtime generator for forged surface: ${name} (${spec.gen})`);
  const made = toSurface(gen(...spec.args, spec.forgeSize));
  cache.set(name, made);
  return made;
}

// ---------------------------------------------------------------------------
// Wiring: triplanar sampling.

export interface TriplanarOptions {
  /**
   * Sample the ORM sheet — roughness, metalness and AO.
   *
   * ON BY DEFAULT AS OF THE MATERIAL-RESPONSE PASS, and the flip is the single
   * largest thing in it. It used to default off "because the cavity is already
   * in the albedo", which is true and beside the point: the G and B channels
   * are the material's RESPONSE, and twenty-nine of the sixty-two dressed
   * materials in this client — every plank, every awning, every boulder, the
   * settlement's own stucco, the sand and the roof tiles — were leaving them
   * unread and rendering at whatever scalar roughness the call site happened
   * to type. Ninety of those scalars were 0.9, 0.95 or 1.0. That is the
   * mechanism behind "every surface reads as one matte BRDF with a noise
   * overlay": the sheets disagreed about light and the shader never asked.
   *
   * It is not free — three more texture fetches — which is why the sampler
   * below now hoists its derivatives and skips planes whose blend weight is
   * negligible. On the axis-aligned kit that pays for the ORM twice over.
   */
  orm?: boolean;
  /** Normal-map gain. 0 disables the normal fetch entirely. */
  normalScale?: number;
  /** AO gain applied to indirect light (requires `orm`). */
  ao?: number;
  /** Tile size override, metres. Keep to powers of two — see the header. */
  meters?: number;
  /**
   * Blend sharpness. 1 = a soft blend over the whole hemisphere; higher
   * collapses axis-aligned faces onto a single plane, which is both crisper
   * and cheaper. The settlement kit is boxes, so it runs sharp.
   */
  sharpen?: number;
  /**
   * Skip a projection plane whose blend weight is negligible. On by default,
   * because most of this world is boxes and on a box two of the three planes
   * contribute nothing.
   *
   * TURN IT OFF FOR CURVED GEOMETRY, and the reason is a real measurement
   * rather than a preference. Skipping a fetch inside divergent control flow
   * requires explicit gradients — GLSL leaves derivatives undefined there —
   * and `textureGrad` gives up the hardware's own LOD path to get them. On a
   * cylinder or an icosahedron no weight is ever small, so the branch never
   * fires and the material pays the gradients for nothing: the fen's trunks
   * and rocks measured +0.8 ms of p95 that way, on the one vista in the game
   * that sits ON the 60 fps gate.
   */
  branch?: boolean;
}

/**
 * Dress a standard material with a surface, projected triplanar in world
 * space.
 *
 * Why triplanar and not UVs: the frontier is built from BoxGeometry,
 * CylinderGeometry, merged carts and icosahedron boulders, at sizes from a
 * 6 cm pebble to a 22 m backdrop block. UV-mapped tiling on that kit means
 * per-mesh repeat values (so no shared material, so a draw call per box) and
 * a visible scale change on every face of every non-cube. World-space
 * triplanar gives every one of them the same real texel density from one
 * shared material, with no seams and no UV authoring.
 *
 * Why world space is safe under a floating origin: the render root shifts on
 * a 128 m grid, and every tile size here divides 128, so a rebase moves the
 * projection by a whole number of tiles — an identity.
 */
export function triplanar(
  material: THREE.MeshStandardMaterial,
  surf: Surface,
  opts: TriplanarOptions = {},
): THREE.MeshStandardMaterial {
  const useOrm = opts.orm ?? true;
  const normalScale = opts.normalScale ?? 1;
  const useNormal = normalScale > 0;
  const aoStrength = opts.ao ?? 1;
  const meters = opts.meters ?? surf.meters;
  const sharpen = opts.sharpen ?? 4;
  const branch = opts.branch ?? true;
  const key = `bebop-tri|${useOrm ? 1 : 0}|${useNormal ? 1 : 0}|${sharpen}|${branch ? 1 : 0}`;
  // Two shapes of the same fetch, chosen when the program is built. See
  // TriplanarOptions.branch for why a curved surface takes the second one.
  const triDerivs = branch
    ? "vec3 triDx = dFdx( triP );\n        vec3 triDy = dFdy( triP );"
    : "vec3 triDx = vec3( 0.0 );\n        vec3 triDy = vec3( 0.0 );";
  const triFetch = branch
    ? [
        "if ( w.x > 0.0025 ) s += textureGrad( sheet, p.zy, dpx.zy, dpy.zy ) * w.x;",
        "if ( w.y > 0.0025 ) s += textureGrad( sheet, p.xz, dpx.xz, dpy.xz ) * w.y;",
        "if ( w.z > 0.0025 ) s += textureGrad( sheet, p.xy, dpx.xy, dpy.xy ) * w.z;",
      ].join("\n          ")
    : [
        "s += texture2D( sheet, p.zy ) * w.x;",
        "s += texture2D( sheet, p.xz ) * w.y;",
        "s += texture2D( sheet, p.xy ) * w.z;",
      ].join("\n          ");
  const normalFetch = branch
    ? [
        "if ( triW.x > 0.0025 ) nx = textureGrad( uTriNormal, triP.zy, triDx.zy, triDy.zy ).xyz * 2.0 - 1.0;",
        "if ( triW.y > 0.0025 ) ny = textureGrad( uTriNormal, triP.xz, triDx.xz, triDy.xz ).xyz * 2.0 - 1.0;",
        "if ( triW.z > 0.0025 ) nz = textureGrad( uTriNormal, triP.xy, triDx.xy, triDy.xy ).xyz * 2.0 - 1.0;",
      ].join("\n          ")
    : [
        "nx = texture2D( uTriNormal, triP.zy ).xyz * 2.0 - 1.0;",
        "ny = texture2D( uTriNormal, triP.xz ).xyz * 2.0 - 1.0;",
        "nz = texture2D( uTriNormal, triP.xy ).xyz * 2.0 - 1.0;",
      ].join("\n          ");
  // THE BLEND WEIGHT, AS A MULTIPLY CHAIN. `pow(abs(n), vec3(uTriSharpen))` is
  // three transcendentals per fragment on every triplanar surface in the game,
  // and the exponent is an integer that is already part of this program's
  // cache key — so it is multiplied out instead. Exact, not an approximation.
  const weightSetup =
    Number.isInteger(sharpen) && sharpen >= 1 && sharpen <= 16
      ? `vec3 triA = abs( triN );\n        vec3 triW = triA;${"\n        triW *= triA;".repeat(sharpen - 1)}`
      : "vec3 triW = pow( abs( triN ), vec3( uTriSharpen ) );";

  // Sheen is a second BRDF lobe, not a uniform — see clothMaterial(). A
  // surface that declares one and is handed a material that cannot carry it
  // would render as cardboard, silently, in exactly the frames the exercise is
  // judged on, so it fails at scene build instead.
  const sheen = surf.response.sheen;
  if (sheen > 0) {
    const phys = material as Partial<THREE.MeshPhysicalMaterial>;
    if (phys.sheen === undefined) {
      throw new Error(
        `${surf.response.cls} declares sheen ${sheen} — build it with clothMaterial(), not MeshStandardMaterial`,
      );
    }
    phys.sheen = sheen;
    // Woven fibre ends scatter WIDE; a tight sheen lobe is a satin, and this
    // is sailcloth. The tint is the sheet's own hue held at full value: sheen
    // is a surface scatter, so it does not take the dye the body colour does.
    phys.sheenRoughness = 0.85;
    phys.sheenColor = new THREE.Color(0xffffff);
  }

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTriAlbedo = { value: surf.albedo };
    shader.uniforms.uTriScale = { value: 1 / meters };
    shader.uniforms.uTriSharpen = { value: sharpen };
    // The half of the BRDF that is not a texture. Both are uniforms on every
    // triplanar program rather than compile-time variants: two ALU ops is
    // cheaper than a second program, and one shape is easier to reason about.
    shader.uniforms.uTriSpec = { value: surf.response.specular };
    shader.uniforms.uTriEnv = { value: surf.response.env };
    if (useNormal) {
      shader.uniforms.uTriNormal = { value: surf.normal };
      shader.uniforms.uTriNormalScale = { value: normalScale };
    }
    if (useOrm) {
      shader.uniforms.uTriOrm = { value: surf.orm };
      shader.uniforms.uTriAo = { value: aoStrength };
    }

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        /* glsl */ `
        #include <common>
        varying vec3 vTriP;
        varying vec3 vTriN;
        `,
      )
      .replace(
        "#include <begin_vertex>",
        /* glsl */ `
        #include <begin_vertex>
        vec4 triLocal = vec4( transformed, 1.0 );
        vec3 triNormal = objectNormal;
        #ifdef USE_INSTANCING
          triLocal = instanceMatrix * triLocal;
          triNormal = mat3( instanceMatrix ) * triNormal;
        #endif
        vTriP = ( modelMatrix * triLocal ).xyz;
        vTriN = mat3( modelMatrix ) * triNormal;
        `,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        /* glsl */ `
        #include <common>
        varying vec3 vTriP;
        varying vec3 vTriN;
        uniform sampler2D uTriAlbedo;
        uniform float uTriScale;
        uniform float uTriSharpen;
        uniform float uTriSpec;
        uniform float uTriEnv;
        ${useNormal ? "uniform sampler2D uTriNormal;\nuniform float uTriNormalScale;" : ""}
        ${useOrm ? "uniform sampler2D uTriOrm;\nuniform float uTriAo;" : ""}
        // THE SAMPLER, and both of its departures from the obvious one.
        //
        // THE BRANCH. Triplanar's standing cost is that it fetches every sheet
        // three times whether or not the plane contributes. At sharpen 4 — the
        // settlement kit is boxes — a face's dominant weight is ~1.0 and the
        // other two are ~1e-4, so two thirds of every fetch on the largest
        // surfaces in the game was being multiplied by nothing and added. The
        // threshold is 1/400th of the blend: what it drops is below the eighth
        // bit of the result, and it is what pays for reading the ORM sheet at
        // all (see TriplanarOptions.orm).
        //
        // EXPLICIT GRADIENTS, handed down from the caller where they are taken
        // in uniform flow. GLSL leaves a derivative inside divergent control
        // flow undefined, and a mip level is exactly what derivatives decide,
        // so the branch above is only legal with them. They are not free —
        // textureGrad gives up the hardware's own LOD path — and the fen
        // measured the bill: on a trunk, which is a cylinder, no plane's
        // weight is ever small enough to skip, so it pays for the gradients
        // and collects nothing — so a curved surface takes the unbranched
        // shape instead (see TriplanarOptions.branch).
        vec4 bebopTri( sampler2D sheet, vec3 p, vec3 dpx, vec3 dpy, vec3 w ) {
          vec4 s = vec4( 0.0 );
          ${triFetch}
          return s;
        }
        `,
      )
      .replace(
        "#include <map_fragment>",
        /* glsl */ `
        vec3 triN = normalize( vTriN );
        ${weightSetup}
        triW /= max( triW.x + triW.y + triW.z, 1e-4 );
        vec3 triP = vTriP * uTriScale;
        ${triDerivs}
        diffuseColor *= bebopTri( uTriAlbedo, triP, triDx, triDy, triW );
        ${useOrm ? "vec4 triOrm = bebopTri( uTriOrm, triP, triDx, triDy, triW );" : ""}
        `,
      )
      .replace(
        "#include <roughnessmap_fragment>",
        useOrm
          ? "float roughnessFactor = roughness * triOrm.g;"
          : "float roughnessFactor = roughness;",
      )
      .replace(
        "#include <metalnessmap_fragment>",
        useOrm
          ? "float metalnessFactor = metalness * triOrm.b;"
          : "float metalnessFactor = metalness;",
      );

    if (useNormal) {
      // Whiteout blend: perturb each plane's tangent normal into world space,
      // then blend. Doing it here rather than through three's normalMap slot
      // is what lets one sheet dress a box, a cylinder and a boulder — three
      // would need per-face tangents that none of this geometry carries.
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <normal_fragment_maps>",
        /* glsl */ `
        {
          // Same weight branch as the sampler above, and the same reasoning:
          // a plane whose blend weight is under 1/400th contributes less than
          // the eighth bit of the result, and this is the second-largest
          // fetch cost in the frame after the albedo. A skipped plane must
          // still be a legal tangent normal, not a zero vector, because the
          // whiteout blend below adds all three before it normalises.
          vec3 nx = vec3( 0.0, 0.0, 1.0 );
          vec3 ny = nx;
          vec3 nz = nx;
          ${normalFetch}
          nx.xy *= uTriNormalScale;
          ny.xy *= uTriNormalScale;
          nz.xy *= uTriNormalScale;
          vec3 an = vec3( normal );
          nx = vec3( nx.xy + triN.zy, abs( nx.z ) * triN.x );
          ny = vec3( ny.xy + triN.xz, abs( ny.z ) * triN.y );
          nz = vec3( nz.xy + triN.xy, abs( nz.z ) * triN.z );
          vec3 worldN = normalize( nx.zyx * triW.x + ny.xzy * triW.y + nz.xyz * triW.z );
          vec3 viewN = normalize( ( viewMatrix * vec4( worldN, 0.0 ) ).xyz );
          // Keep the geometric hemisphere: a flat-shaded boulder's facet must
          // not be turned inside out by a sheet that knows nothing about it.
          normal = normalize( mix( an, viewN, 0.85 ) );
        }
        `,
      );
    }

    if (useOrm) {
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <aomap_fragment>",
        /* glsl */ `
        {
          float triAo = ( triOrm.r - 1.0 ) * uTriAo + 1.0;
          reflectedLight.indirectDiffuse *= triAo;
          #if defined( USE_ENVMAP ) && defined( STANDARD )
            float triNV = saturate( dot( geometryNormal, geometryViewDir ) );
            reflectedLight.indirectSpecular *= computeSpecularOcclusion( triNV, triAo, material.roughness );
          #endif
        }
        `,
      );
    }

    // DIELECTRIC REFLECTANCE. three hardcodes F0 = 0.04 for every
    // MeshStandardMaterial in existence — a reasonable average of common
    // dielectrics, and wrong at both ends of this catalog. Lime plaster and
    // dry canvas sit near 0.026 and read chalkier for it; fired pantile, damp
    // paving and window glass sit at 0.05–0.09 and are the surfaces that
    // should be catching an edge of sky when the plaster beside them is not.
    // One uniform, no map, no bytes — and it is the term that gives glazing
    // back the fresnel that metalness 0.55 had been standing in for.
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <lights_physical_fragment>",
      /* glsl */ `
      #include <lights_physical_fragment>
      material.specularColor = vec3( uTriSpec );
      material.specularColorBlended = mix( material.specularColor, diffuseColor.rgb, metalnessFactor );
      `,
    );

    // …and how much of the environment probe this class returns. Deliberately
    // NOT material.envMapIntensity, which scales the probe's irradiance too:
    // raising that on a rusted bar or a plaster wall raises its ambient fill
    // and moves a grade six rounds of critics signed off on. `radiance` at
    // this point holds only the IBL specular, so a polished metal can mirror
    // the sun disc at 6× while the oxide beside it — same sheet, same
    // material, one texel away — lights exactly as it did before.
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <lights_fragment_maps>",
      /* glsl */ `
      #include <lights_fragment_maps>
      radiance *= uTriEnv;
      `,
    );
  };
  // Materials that share this code share a program; their uniforms (and so
  // their sheets) stay their own. Without an explicit key three would key on
  // the closure's SOURCE, which is identical for every variant here.
  material.customProgramCacheKey = () => key;
  return material;
}

/**
 * A grey tint that makes `surf` land at the same luminance the flat colour
 * `hex` used to render at, leaving the sheet's own hue intact.
 *
 * This is the whole reason the grade survives an asset pass. Six rounds tuned
 * every anchor against flat albedos; swapping in a real material with its own
 * colour would move every one of them. Dividing the target's linear luminance
 * by the sheet's mean is exactly energy-preserving: the surface now has hue,
 * value structure and relief, and its AVERAGE is the number the critics
 * already signed off.
 *
 * It is also what carries the grade across the move offline. `meanLinear` for
 * a shipped surface is measured by the BAKER and travels in the manifest —
 * the client never re-measures a sheet it did not make. Measured on the
 * stucco: 0.7600 at 256², 512², 1024² and 2048². The grade does not move.
 */
export function matchLuma(surf: Surface, hex: number): THREE.Color {
  const target = new THREE.Color(hex);
  // THREE.Color from hex is sRGB-decoded to linear on assignment when the
  // renderer's working space is linear, which it is.
  const lum = 0.2126 * target.r + 0.7152 * target.g + 0.0722 * target.b;
  const k = Math.min(4, lum / Math.max(1e-4, surf.meanLinear));
  return new THREE.Color().setRGB(k, k, k, THREE.LinearSRGBColorSpace);
}

/**
 * Dress a material with a surface AND balance it against the flat colour it
 * replaces — the two calls that always go together.
 */
export function dress(
  material: THREE.MeshStandardMaterial,
  name: string,
  wasHex: number,
  opts: TriplanarOptions = {},
): THREE.MeshStandardMaterial {
  const surf = surface(name);
  material.color = matchLuma(surf, wasHex);
  return triplanar(material, surf, opts);
}

/**
 * SHEEN — the one term in the response set that is not a uniform, because it
 * is not a tweak to the standard BRDF but a second lobe beside it.
 *
 * A woven cloth scatters off the ENDS of its fibres, which is why an awning
 * has a bright edge exactly where it turns away from the lens and a painted
 * board does not. No roughness value reproduces that: roughness widens the
 * mirror lobe, and this is a retro-reflective one that peaks at grazing
 * incidence. three's `MeshPhysicalMaterial` carries the real Charlie
 * distribution and handles it for both direct and image-based light, so the
 * decision here is only where to pay for it — and the answer is the seven
 * awnings and banners in the game, not everything.
 *
 * `dress()` and `triplanar()` throw if a sheened surface is handed a material
 * that cannot carry it, rather than silently rendering canvas as cardboard.
 */
export function cloth(
  name: string,
  wasHex: number,
  opts: TriplanarOptions = {},
  params: THREE.MeshPhysicalMaterialParameters = {},
): THREE.MeshPhysicalMaterial {
  const surf = surface(name);
  const mat = new THREE.MeshPhysicalMaterial({ roughness: 1, ...params });
  mat.color = matchLuma(surf, wasHex);
  triplanar(mat, surf, opts);
  return mat;
}

/**
 * `dress()` for a PAINTED object: the sheet supplies the structure and the
 * flat colour keeps its HUE, not just its value.
 *
 * The difference matters wherever colour is the identity of the individual
 * prop rather than of the material class. A teal container, an oxide-red one
 * and a bare galvanised one are one material and three boxes; run them
 * through `dress()` and matchLuma hands each of them the same grey, so the
 * yard turns monochrome and you have traded a palette for a normal map. Here
 * the tint is the object's own colour divided by the sheet's mean — exactly
 * as energy-preserving as matchLuma, and for exactly the same reason: the
 * grade is done and the AVERAGE must not move.
 *
 * Only ever point this at a NEUTRAL sheet (`container`, `plate`). Aimed at a
 * coloured one it multiplies hue by hue, which is how brass becomes mud.
 */
export function paint(
  material: THREE.MeshStandardMaterial,
  name: string,
  hex: number,
  opts: TriplanarOptions = {},
): THREE.MeshStandardMaterial {
  const surf = surface(name);
  const k = Math.min(4, 1 / Math.max(1e-4, surf.meanLinear));
  material.color = new THREE.Color(hex).multiplyScalar(k);
  return triplanar(material, surf, opts);
}
